import { z } from "zod";

const step1Schema = z.object({
  title: z
    .string()
    .trim()
    .min(1, "Title is required")
    .max(200, "Title cannot exceed 200 characters"),
  shortDescription: z
    .string()
    .max(200, "Short description cannot exceed 200 characters")
    .optional(),
  description: z
    .string()
    .max(2000, "Description cannot exceed 2000 characters")
    .optional(),
  date: z.string().min(1, "Event date is required"),
  time: z.string().min(1, "Event time is required"),
  thumbnail: z.any().optional(),
});

const step2Schema = z.object({
  pincode: z
    .string()
    .trim()
    .min(1, "Pincode is required")
    .max(10, "Pincode cannot exceed 10 characters"),
  country: z.string().trim().min(1, "Country is required"),
  state: z.string().trim().min(1, "State is required"),
  city: z.string().trim().min(1, "City is required"),
  venue: z.string().trim().min(1, "Venue is required").max(500, "Venue cannot exceed 500 characters"),
  location: z
    .string()
    .trim()
    .min(1, "Location is required")
    .max(500, "Location cannot exceed 500 characters"),
  whatToExpect: z
    .string()
    .max(1000, "What to expect cannot exceed 1000 characters")
    .optional(),
  tags: z.array(z.string()).max(20, "Cannot have more than 20 tags").optional(),
});

// Helper function to convert string to number or keep as empty string
const toNumberOrEmpty = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const num = Number(val);
  return isNaN(num) ? "" : num;
};

// Price object schema for nested pricing
const priceObjectSchema = z.object({
  price: z
    .union([z.string(), z.number()])
    .transform(toNumberOrEmpty)
    .refine((val) => val === "" || val >= 0, "Price cannot be negative")
    .optional()
    .or(z.literal("")),
  discountedPrice: z
    .union([z.string(), z.number()])
    .transform(toNumberOrEmpty)
    .refine((val) => val === "" || val >= 0, "Discounted price cannot be negative")
    .optional()
    .or(z.literal("")),
}).refine(
  (data) => {
    // If both are empty, it's valid
    if (data.price === "" && data.discountedPrice === "") return true;
    
    // If only discounted price is provided, it's invalid
    if (data.price === "" && data.discountedPrice !== "") {
      return false;
    }
    
    // If both have values, discounted must be less than or equal to regular
    if (data.price !== "" && data.discountedPrice !== "") {
      return Number(data.discountedPrice) <= Number(data.price);
    }
    
    return true;
  },
  {
    message: "Discounted price cannot be greater than regular price",
    path: ["discountedPrice"],
  }
);

// Age restrictions schema
const ageSchema = z.object({
  minAge: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => {
      // Convert empty strings, null, undefined to empty string
      if (val === "" || val === null || val === undefined) return "";
      // Convert to string if it's a number
      return String(val);
    })
    .refine(
      (val) => {
        // Empty is valid
        if (val === "") return true;
        // Must be a valid number string
        const num = Number(val);
        return !isNaN(num) && num >= 0 && Number.isInteger(num);
      },
      "Minimum age must be a valid non-negative integer"
    )
    .or(z.literal("")),
  maxAge: z
    .union([z.string(), z.number()])
    .optional()
    .transform((val) => {
      // Convert empty strings, null, undefined to empty string
      if (val === "" || val === null || val === undefined) return "";
      // Convert to string if it's a number
      return String(val);
    })
    .refine(
      (val) => {
        // Empty is valid
        if (val === "") return true;
        // Must be a valid number string
        const num = Number(val);
        return !isNaN(num) && num >= 0 && Number.isInteger(num);
      },
      "Maximum age must be a valid non-negative integer"
    )
    .or(z.literal("")),
}).refine(
  (data) => {
    // If both are empty, it's valid
    if (data.minAge === "" && data.maxAge === "") return true;
    
    // If only one is provided, it's valid
    if (data.minAge === "" || data.maxAge === "") return true;
    
    // If both have values, minAge must be less than or equal to maxAge
    if (data.minAge !== "" && data.maxAge !== "") {
      const minAge = Number(data.minAge);
      const maxAge = Number(data.maxAge);
      
      // Check if both are valid numbers
      if (isNaN(minAge) || isNaN(maxAge)) return false;
      
      return minAge <= maxAge;
    }
    
    return true;
  },
  {
    message: "Minimum age cannot be greater than maximum age",
    path: ["minAge"],
  }
).refine(
  (data) => {
    // Check that ages don't exceed reasonable limits (e.g., 120)
    if (data.minAge !== "" && Number(data.minAge) > 120) return false;
    if (data.maxAge !== "" && Number(data.maxAge) > 120) return false;
    return true;
  },
  {
    message: "Age cannot exceed 120 years",
    path: ["maxAge"],
  }
);

const step3Schema = z.object({
  audience: z.enum(["all", "adults", "children", "teens", "seniors", "professionals"]),
  
  // NEW: Age restrictions
  age: ageSchema.optional().default({ minAge: "", maxAge: "" }),
  
  capacity: z
    .union([z.string(), z.number()])
    .optional()
    .refine((val) => !val || val === "" || Number(val) >= 1, "Capacity must be at least 1"),
  
  // Nested male pricing
  malePrice: priceObjectSchema,
  
  // Nested female pricing
  femalePrice: priceObjectSchema,
  
  communityLinks: z
    .string()
    .max(2000, "Community links cannot exceed 2000 characters")
    .optional()
    .or(z.literal("")),

  currency: z.string().length(3, "Currency must be a 3-letter code"),
  status: z.enum(["draft", "published", "cancelled", "completed"]),
  showDummyUsers: z.boolean().optional().default(false),
  appliedGst: z.boolean().optional().default(false),
});

export { step1Schema, step2Schema, step3Schema };
